import {
	alephToast,
	AvatarPixelSizes,
	AvatarSize,
	NotificationType,
	isReddit,
	isSpotify,
	NotificationToast,
	translate,
} from '@aleph/shared-components';
import DefaultRedditReportsCellRenderer from 'wb-reddit/app/routes/reports/create/cellRenderers/default';
import RedditReportsHeader from 'wb-reddit/app/routes/reports/create/cellRenderers/header';
import { divideCurrencyFields } from 'wb-reddit/app/routes/superGrid/common/constants';
import RedditReportStatCellRenderer from 'wb-reddit/app/routes/reports/create/cellRenderers/stat';
import RedditReportDateCellRenderer from 'wb-reddit/app/routes/reports/create/cellRenderers/date';
import {
	Maybe,
	RedditAdvertiserListQueryItems,
	ReportApiFiltersOptionsType,
	ReportDateType,
	RedditUsersListQueryItems,
	RedditUsersListQueryUserGroups,
	RedditAdvertiserListQueryUserGroups,
	RedditUsersAndGroupsQueryFullEmailUsers,
	RedditReportsQueryItems,
} from 'wb-reddit/graphql/generated';
import {
	UserGroupAccess, User, AdvertiserAccount, UserGroup,
} from 'wb-reddit/graphql/authgraph/generated';
import { RedditReport } from 'wb-reddit/app/routes/reports/components/report';
import { ColDef } from 'ag-grid-community';
import { AuthGraphAdvertiserListQueryItems } from 'wb-spotify/graphql/authgraph/generated';
import { SpotifyReportSchedule } from 'wb-spotify/app/common/utils';
import { captureException } from '@sentry/react';

export type RedditAccessEntity =
Pick<RedditUsersAndGroupsQueryFullEmailUsers, 'id' | 'userFullName' | '__typename' | 'value'>	// SearchUser
| RedditAdvertiserListQueryUserGroups	// UserGroup
| RedditUsersListQueryUserGroups	// UserGroup
| RedditUsersListQueryItems & { profilePictureUrl: string, id: string, userFullName: string } // User
| RedditAdvertiserListQueryItems // AgencyAccount;
| AuthGraphAdvertiserListQueryItems;

export type AuthAccessEntity = UserGroupAccess | User | AdvertiserAccount | UserGroup;

export type ReportRow = RedditReportsQueryItems;

const getRedditAccessEntityName = (entity: RedditAccessEntity) => {
	if (entity.__typename === 'SearchUser' || entity.__typename === 'User') {
		return `${entity.userFullName}`;
	}

	if (entity.__typename === 'UserGroup') {
		return entity.userGroupName;
	}

	if (entity.__typename === 'AgencyAccount') {
		return entity.agencyAccountName;
	}

	return 'Unknown';
};

export const getAccessEntityName = (entity: RedditAccessEntity | AuthAccessEntity): string => {
	if (isReddit()) {
		return getRedditAccessEntityName(entity as RedditAccessEntity);
	}

	if (entity.__typename === 'UserGroupAccess') {
		return entity.userGroup.userGroupName;
	}

	if (entity.__typename === 'User') {
		return `${entity.userName} ${entity.userSurname || ''}`;
	}

	if (entity.__typename === 'AdvertiserAccount') {
		return (entity as AuthGraphAdvertiserListQueryItems).agencyAccountName;
	}

	if (entity.__typename === 'UserGroup') {
		return entity.userGroupName;
	}

	return 'Unknown';
};

export const downloadRedditReport = async (report: RedditReport) => {
	try {
		const network = isReddit() ? 'pn_re' : 'pn_sp';
		const reportContentCSV = await fetch(
			`${import.meta.env.VITE_API_PATH}/${network}/report/${report.reportApi.reportApiToken}/lang/en/format/csv`,
		);

		const downloadLink = document.createElement('a');
		downloadLink.download = `${report.reportName}.csv`;
		downloadLink.href = window.URL?.createObjectURL?.(await reportContentCSV.blob());
		downloadLink.click();
	} catch (e) {
		captureException(e);

		alephToast(
			NotificationToast({
				type:  NotificationType.Error,
				title: translate('re: reports.download_err', { defaultValue: 'Error downloading report' }),
			}),
		);
	}
};

export const REDDIT_REPORT_BREAKDOWN_OPTIONS = [
	// TODO put translations
	{ value: 'agencyAccountName', label: 'Advertiser' },
	{ value: 'adAccountName', label: 'Ad Account' },
	{ value: 'campaignName', label: 'Campaign' },
	{ value: 'groupName', label: 'Group' },
	{ value: 'adName', label: 'Ad' },

	{ value: 'date', label: 'Date' },
	{ value: 'interest', label: 'Interest', exclude: ['community', 'country', 'region'] },
	{ value: 'community', label: 'Community', exclude: ['interest', 'country', 'region'] },
	{ value: 'country', label: 'Country', exclude: ['interest', 'community'] },
	{ value: 'region', label: 'Region', exclude: ['interest', 'community'] },
];

export const REDDIT_REPORTS_DEFAULT_COL_DEF: ColDef = {
	cellClass: params => {
		const currNode = params.node;
		const prevNode = params.api.getDisplayedRowAtIndex((params.node?.rowIndex ?? 0) - 1);

		const isSameAsPrev = currNode?.data?.[params.colDef?.field ?? '']
			=== prevNode?.data?.[params.colDef?.field ?? ''];

		if (isSameAsPrev) {
			return 'report-cell-hidden';
		}

		return null;
	},
	cellRenderer:    RedditReportStatCellRenderer,
	headerComponent: RedditReportsHeader,
	headerClass:     'border-r border-r-gray-300',
	cellStyle:       {
		borderRight: '1px solid hsl(var(--gray-300))',
		padding:     0,
	},
	minWidth: 200,
};

export const REDDIT_REPORT_COL_DEFS: ColDef[] = [
	{
		field:        'index',
		headerName:   '#',
		valueGetter:  params => (params.node?.rowIndex ?? 0) + 1,
		minWidth:     32,
		maxWidth:     32,
		cellClass:    'text-gray-600 flex items-center justify-center',
		headerClass:  'flex justify-center !px-[4px]',
		pinned:       'left',
		cellRenderer: DefaultRedditReportsCellRenderer,
	},
	{
		field:        'agencyAccountName',
		cellRenderer: DefaultRedditReportsCellRenderer,
		pinned:       'left',
		minWidth:     200,
	},
	{
		field:        'adAccountName',
		cellRenderer: DefaultRedditReportsCellRenderer,
		pinned:       'left',
		minWidth:     200,
	},
	{
		field:        'campaignName',
		cellRenderer: DefaultRedditReportsCellRenderer,
		pinned:       'left',
		minWidth:     200,
	},
	{
		field:        'groupName',
		cellRenderer: DefaultRedditReportsCellRenderer,
		pinned:       'left',
		minWidth:     200,
	},
	{
		field:        'adName',
		cellRenderer: DefaultRedditReportsCellRenderer,
		pinned:       'left',
		minWidth:     200,
	},
	{
		field:        'date',
		cellRenderer: RedditReportDateCellRenderer,
		pinned:       'left',
		maxWidth:     200,
		minWidth:     200,
	},
	{
		field:        'interest',
		cellRenderer: DefaultRedditReportsCellRenderer,
		pinned:       'left',
		maxWidth:     200,
		minWidth:     200,
	},
	{
		field:        'community',
		cellRenderer: DefaultRedditReportsCellRenderer,
		pinned:       'left',
		maxWidth:     200,
		minWidth:     200,
	},
	{
		field:        'country',
		cellRenderer: DefaultRedditReportsCellRenderer,
		pinned:       'left',
		maxWidth:     80,
		minWidth:     80,
	},
	{
		field:        'region',
		cellRenderer: DefaultRedditReportsCellRenderer,
		pinned:       'left',
		maxWidth:     200,
		minWidth:     200,
	},
	{
		field:     'impressions',
		cellClass: () => null,
	},
	{ field: 'viewableImpressions', cellClass: () => null },
	{ field: 'clicks', cellClass: () => null },
	{
		field:              'spend',
		cellRendererParams: {
			divisor:     divideCurrencyFields.spend,
			addCurrency: true,
		},
		cellClass: () => null,
	},
	{
		field:              'cpc',
		headerName:         translate('re: columns.cpc', { defaultValue: 'CPC (Cost per click)' }),
		cellRendererParams: {
			divisor:     divideCurrencyFields.spend,
			addCurrency: true,
		},
		cellClass: () => null,
	},
	{
		field:              'ecpm',
		headerName:         translate('re: columns.ecpm', { defaultValue: 'eCPM (Effective cost per mille)' }),
		cellRendererParams: {
			divisor:     divideCurrencyFields.spend,
			addCurrency: true,
		},
		cellClass: () => null,
	},	{
		field:              'ctr',
		headerName:         translate('re: columns.ctr', { defaultValue: 'CTR (Click-through rate)' }),
		cellRendererParams: {
			isRate: true,
		},
		cellClass: () => null,
	},
	{ field: 'videoWatched25Percent', cellClass: () => null },
	{ field: 'videoWatched50Percent', cellClass: () => null },
	{ field: 'videoWatched75Percent', cellClass: () => null },
	{ field: 'videoWatched95Percent', cellClass: () => null },
	{ field: 'videoWatched100Percent', cellClass: () => null },
	{ field: 'videoWatched3Seconds', cellClass: () => null },
	{ field: 'videoWatched5Seconds', cellClass: () => null },
	{ field: 'videoWatched10Seconds', cellClass: () => null },
	{ field: 'videoStarted', cellClass: () => null },
	{ field: 'videoPlaysExpanded', cellClass: () => null },
	{ field: 'videoFullyViewableImpressions', cellClass: () => null },
	{ field: 'videoViewableImpressions', cellClass: () => null },
	{ field: 'videoPlaysWithSound', cellClass: () => null },
	{ field: 'viewerComments', cellClass: () => null },
	{ field: 'commentsPageViews', cellClass: () => null },
	{ field: 'commentUpvotes', cellClass: () => null },
	{ field: 'commentDownvotes', cellClass: () => null },
	{ field: 'conversionPurchaseTotalItems', cellClass: () => null },
	{ field: 'conversionPurchaseTotalValue', cellClass: () => null },
	{ field: 'conversionAddToWishlistTotalItems', cellClass: () => null },
	{ field: 'conversionAddToWishlistTotalValue', cellClass: () => null },
	{ field: 'conversionAddToCartTotalValue', cellClass: () => null },
	{ field: 'conversionAddToCartTotalItems', cellClass: () => null },
	{ field: 'conversionLeadTotalValue', cellClass: () => null },
	{ field: 'conversionSignupTotalValue', cellClass: () => null },
	{ field: 'appInstallMetricsCompletedTutorial', cellClass: () => null },
	{ field: 'appInstallMetricsSpend', cellClass: () => null },
	{ field: 'appInstallMetricsAddToCart', cellClass: () => null },
	{ field: 'appInstallMetricsSignUp', cellClass: () => null },
	{ field: 'appInstallMetricsLevelAchieved', cellClass: () => null },
	{ field: 'appInstallMetricsSpendCredits', cellClass: () => null },
	{ field: 'appInstallMetricsPurchase', cellClass: () => null },
	{ field: 'appInstallMetricsAddPaymentInfo', cellClass: () => null },
	{ field: 'appInstallMetricsInstall', cellClass: () => null },
	{ field: 'appInstallMetricsSearch', cellClass: () => null },
	{ field: 'appInstallMetricsViewContent', cellClass: () => null },
	{ field: 'appInstallMetricsAppLaunch', cellClass: () => null },
	{ field: 'legacyClickConversionsAttributionWindowMonth', cellClass: () => null },
	{ field: 'legacyClickConversionsAttributionWindowWeek', cellClass: () => null },
	{ field: 'legacyClickConversionsAttributionWindowDay', cellClass: () => null },
	{ field: 'legacyViewConversionsAttributionWindowMonth', cellClass: () => null },
	{ field: 'legacyViewConversionsAttributionWindowWeek', cellClass: () => null },
	{ field: 'legacyViewConversionsAttributionWindowDay', cellClass: () => null },
	{ field: 'searchClickThroughConversionsAttributionWindowMonth', cellClass: () => null },
	{ field: 'searchClickThroughConversionsAttributionWindowWeek', cellClass: () => null },
	{ field: 'searchClickThroughConversionsAttributionWindowDay', cellClass: () => null },
	{ field: 'searchViewThroughConversionsAttributionWindowMonth', cellClass: () => null },
	{ field: 'searchViewThroughConversionsAttributionWindowWeek', cellClass: () => null },
	{ field: 'searchViewThroughConversionsAttributionWindowDay', cellClass: () => null },
	{ field: 'viewContentClickThroughConversionsAttributionWindowMonth', cellClass: () => null },
	{ field: 'viewContentClickThroughConversionsAttributionWindowWeek', cellClass: () => null },
	{ field: 'viewContentClickThroughConversionsAttributionWindowDay', cellClass: () => null },
	{ field: 'viewContentViewThroughConversionsAttributionWindowMonth', cellClass: () => null },
	{ field: 'viewContentViewThroughConversionsAttributionWindowWeek', cellClass: () => null },
	{ field: 'viewContentViewThroughConversionsAttributionWindowDay', cellClass: () => null },
	{ field: 'pageVisitClickThroughConversionsAttributionWindowMonth', cellClass: () => null },
	{ field: 'pageVisitClickThroughConversionsAttributionWindowWeek', cellClass: () => null },
	{ field: 'pageVisitClickThroughConversionsAttributionWindowDay', cellClass: () => null },
	{ field: 'pageVisitViewThroughConversionsAttributionWindowMonth', cellClass: () => null },
	{ field: 'pageVisitViewThroughConversionsAttributionWindowWeek', cellClass: () => null },
	{ field: 'pageVisitViewThroughConversionsAttributionWindowDay', cellClass: () => null },
	{ field: 'addToCartClickThroughConversionsAttributionWindowMonth', cellClass: () => null },
	{ field: 'addToCartClickThroughConversionsAttributionWindowWeek', cellClass: () => null },
	{ field: 'addToCartClickThroughConversionsAttributionWindowDay', cellClass: () => null },
	{ field: 'addToCartViewThroughConversionsAttributionWindowMonth', cellClass: () => null },
	{ field: 'addToCartViewThroughConversionsAttributionWindowWeek', cellClass: () => null },
	{ field: 'addToCartViewThroughConversionsAttributionWindowDay', cellClass: () => null },
	{ field: 'signUpClickThroughConversionsAttributionWindowMonth', cellClass: () => null },
	{ field: 'signUpClickThroughConversionsAttributionWindowWeek', cellClass: () => null },
	{ field: 'signUpClickThroughConversionsAttributionWindowDay', cellClass: () => null },
	{ field: 'signUpViewThroughConversionsAttributionWindowMonth', cellClass: () => null },
	{ field: 'signUpViewThroughConversionsAttributionWindowWeek', cellClass: () => null },
	{ field: 'signUpViewThroughConversionsAttributionWindowDay', cellClass: () => null },
	{ field: 'leadClickThroughConversionsAttributionWindowMonth', cellClass: () => null },
	{ field: 'leadClickThroughConversionsAttributionWindowWeek', cellClass: () => null },
	{ field: 'leadClickThroughConversionsAttributionWindowDay', cellClass: () => null },
	{ field: 'leadViewThroughConversionsAttributionWindowMonth', cellClass: () => null },
	{ field: 'leadViewThroughConversionsAttributionWindowWeek', cellClass: () => null },
	{ field: 'leadViewThroughConversionsAttributionWindowDay', cellClass: () => null },
	{ field: 'addToWishlistClickThroughConversionsAttributionWindowMonth', cellClass: () => null },
	{ field: 'addToWishlistClickThroughConversionsAttributionWindowWeek', cellClass: () => null },
	{ field: 'addToWishlistClickThroughConversionsAttributionWindowDay', cellClass: () => null },
	{ field: 'addToWishlistViewThroughConversionsAttributionWindowMonth', cellClass: () => null },
	{ field: 'addToWishlistViewThroughConversionsAttributionWindowWeek', cellClass: () => null },
	{ field: 'addToWishlistViewThroughConversionsAttributionWindowDay', cellClass: () => null },
	{ field: 'purchaseClickThroughConversionsAttributionWindowMonth', cellClass: () => null },
	{ field: 'purchaseClickThroughConversionsAttributionWindowWeek', cellClass: () => null },
	{ field: 'purchaseClickThroughConversionsAttributionWindowDay', cellClass: () => null },
	{ field: 'purchaseViewThroughConversionsAttributionWindowMonth', cellClass: () => null },
	{ field: 'purchaseViewThroughConversionsAttributionWindowWeek', cellClass: () => null },
	{ field: 'purchaseViewThroughConversionsAttributionWindowDay', cellClass: () => null },
	{ field: 'customClickThroughConversionsAttributionWindowMonth', cellClass: () => null },
	{ field: 'customClickThroughConversionsAttributionWindowWeek', cellClass: () => null },
	{ field: 'customClickThroughConversionsAttributionWindowDay', cellClass: () => null },
	{ field: 'customViewThroughConversionsAttributionWindowMonth', cellClass: () => null },
	{ field: 'customViewThroughConversionsAttributionWindowWeek', cellClass: () => null },
	{ field: 'customViewThroughConversionsAttributionWindowDay', cellClass: () => null },
];

// TODO put translations
export const SCHEDULER_OPTIONS = [
	{ value: ReportDateType.Yesterday, label: 'Daily' },
	{ value: ReportDateType.PreviousWeek, label: 'Weekly' },
	// { value: ReportDateType.Biweekly, label: 'Biweekly' },
	{ value: ReportDateType.PreviousMonth, label: 'Monthly' },
	{ value: ReportDateType.Previous_3Months, label: 'Quarterly' },
	// { value: ReportDateType.PreviousYear, label: 'Yearly' },
];

export const getScheduleName = (selectedSchedule?: ReportDateType) => {
	if (isSpotify()) {
		const selected = selectedSchedule as unknown as SpotifyReportSchedule;

		switch (selected) {
			case SpotifyReportSchedule.OnlyOnce:
				return 'Day';
			case SpotifyReportSchedule.EveryMonday:
				return 'Monday';
			case SpotifyReportSchedule.EveryWeekday:
				return 'Sunday';
			case SpotifyReportSchedule.FirstOfTheMonth:
				return 'First day of the month';
			default:
				return null;
		}
	}

	switch (selectedSchedule) {
		case ReportDateType.Yesterday:
			return 'Day';
		case ReportDateType.PreviousWeek:
			return 'Monday';
		case ReportDateType.Biweekly:
			return 'Second monday';
		case ReportDateType.PreviousMonth:
		case ReportDateType.Previous_3Months:
		case ReportDateType.PreviousYear:
			return 'First day of the month';
	}

	return null;
};

export const getScheduleButtonName = (selectedSchedule?: ReportDateType) => {
	if (isSpotify()) {
		// const selected = selectedSchedule as unknown as SpotifyReportSchedule;
		//
		// switch (selected) {
		// 	case SpotifyReportSchedule.OnlyOnce:
		// 		return 'Day';
		// 	case SpotifyReportSchedule.EveryMonday:
		// 		return 'Monday';
		// 	case SpotifyReportSchedule.EveryWeekday:
		// 		return 'Sunday';
		// 	case SpotifyReportSchedule.FirstOfTheMonth:
		// 		return 'First day of the month';
		// 	case SpotifyReportSchedule.Unknown:
		// 	default:
		return undefined;
		// }
	}

	switch (selectedSchedule) {
		case ReportDateType.PreviousWeek:
			return 'Monday';
		case ReportDateType.Biweekly:
			return 'Every second monday';
		case ReportDateType.PreviousMonth:
		case ReportDateType.Previous_3Months:
			return 'the 1st';
		case ReportDateType.PreviousYear:
			return '1.1.';
	}

	return undefined;
};

export const getReportClientIds = (
	reportFilterOptions?: Maybe<Array<Maybe<ReportApiFiltersOptionsType>>>,
): string[] => {
	const filterId = isReddit() ? 'clientId' : 'filter';

	return (reportFilterOptions ?? []).find(filter => filter?.filter === filterId)?.value ?? [];
};

export const getAvatarFallbackSize = (size?: AvatarSize) => (size ? AvatarPixelSizes[size] : 0);
